import classNames from 'classnames';
import { useMemo } from 'react';

import Card from '../../../../../global/Mui/Card';
import CheckSvg from '../../../../../global/SvgComponents/Check_svg';
import { filterEnabledItems } from '../../../../../utils/utils';
import {
  SUBSCRIPTION_PAYMENT_TYPES,
  getOrderSubtotal,
} from '../../../../../utils/subscriptions';
import {
  getFilteredMembershipItems,
  MEGA_KIT_MEMBERSHIP_ORDER_ITEMS,
} from '../../../../../utils/membership';
import { useSubscription } from '../../../SubscriptionProvider';

import './SubscriptionSelectionOptions.scss';

const UNLIMITED_MEMBERSHIP_TITLE = 'Unlimited Membership';
const UNLIMITED_TAG_FALLBACK = 'SAVE $30 — BEST VALUE';
const UNLIMITED_SUBTITLE = 'per year | unlimited everything';
const UNLIMITED_VALUE_FALLBACK = 68;
const UNLIMITED_BREAKEVEN_COPY = 'Membership pays for itself at bag 10!';
const UNLIMITED_CTA_COPY = 'Get Unlimited →';
const UNLIMITED_BENEFITS_FALLBACK = [
  'Unlimited Take Back Bags',
  'Unlimited Tech boxes',
  'Free shipping, always',
  'Milestone jackpots and bonuses',
];
const ONE_TIME_TITLE = 'The Mega Kit';
const ONE_TIME_TAG = 'One-Time Purchase';
const ONE_TIME_VALUE_FALLBACK = 98;
const ONE_TIME_SUBTITLE = 'one-time · 4 bags + tech box';
const ONE_TIME_BENEFITS_FALLBACK = [
  '4 Single Take Back Bags',
  '1 Tech Box',
  'Free Shipping',
];
const ONE_TIME_WARNING_COPY = "You're leaving a lot of rewards on the table.";
const ONE_TIME_CTA_COPY = 'Get the mega kit';

const SubscriptionSelectionOptions = ({
  section = {},
  selected = null,
  onSelect = () => {},
  onContinue = () => {},
}) => {
  const sectionItems = filterEnabledItems(section.items);

  if (!sectionItems?.length) {
    return null;
  }

  return (
    <div className="SubscriptionSelectionOptions">
      {sectionItems.map(item => (
        <SubscriptionSelectionOption
          key={item.uid}
          item={item}
          selected={selected === item.uid}
          onSelect={onSelect}
          onContinue={onContinue}
        />
      ))}
    </div>
  );
};

const SubscriptionSelectionOption = ({
  item = {},
  selected = false,
  onSelect,
  onContinue,
}) => {
  const {
    uid, type,
  } = item;

  const { subscriptionPlan, productPrices } = useSubscription();

  const membershipOneTimeIds = useMemo(
    () => getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME).map(m => m.id),
    [],
  );

  const megaKitSubtotal = useMemo(
    () => getOrderSubtotal(
      MEGA_KIT_MEMBERSHIP_ORDER_ITEMS,
      productPrices,
      membershipOneTimeIds,
    ),
    [productPrices, membershipOneTimeIds],
  );

  const {
    value: subscriptionValue,
    prevValue: subscriptionPrevValue,
  } = subscriptionPlan || {};

  const unlimitedPrice = typeof subscriptionValue === 'number' && subscriptionValue >= 0
    ? subscriptionValue
    : UNLIMITED_VALUE_FALLBACK;

  const oneTimePrice = megaKitSubtotal > 0 ? megaKitSubtotal : ONE_TIME_VALUE_FALLBACK;

  const saveVsList = typeof subscriptionPrevValue === 'number'
    && typeof subscriptionValue === 'number'
    && subscriptionPrevValue > subscriptionValue
    ? Math.round(subscriptionPrevValue - subscriptionValue)
    : null;

  const unlimitedTag = saveVsList
    ? `SAVE $${saveVsList} — BEST VALUE`
    : UNLIMITED_TAG_FALLBACK;

  const handleCtaClick = (e) => {
    e.stopPropagation();
    onSelect(uid, type);
    onContinue(type);
  };

  const isOneTimePurchase = item.type === SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME;
  const tagLabel = isOneTimePurchase ? ONE_TIME_TAG : unlimitedTag;

  const unlimitedBenefits = Array.isArray(item.benefits) && item.benefits.length > 0
    ? item.benefits
    : UNLIMITED_BENEFITS_FALLBACK;

  const oneTimeBenefits = Array.isArray(item.benefits) && item.benefits.length > 0
    ? item.benefits
    : ONE_TIME_BENEFITS_FALLBACK;

  const membershipTitle = item.cardTitle || item.title
    || (isOneTimePurchase ? ONE_TIME_TITLE : UNLIMITED_MEMBERSHIP_TITLE);

  return (
    <div
      className={classNames('SubscriptionSelectionOption', {
        'SubscriptionSelectionOption--subscription': !isOneTimePurchase,
        'SubscriptionSelectionOption--onetime': isOneTimePurchase,
      })}
    >
      <Card
        key={`SubscriptionSelectionOption-${uid}`}
        className={classNames('SubscriptionSelectionOption__card', {
          withTag: true,
          'SubscriptionSelectionOption__card--subscription': !isOneTimePurchase,
          'SubscriptionSelectionOption__card--onetime': isOneTimePurchase,
        })}
        variant="primary"
        selected={selected}
        onClick={() => onSelect(uid, type)}
      >
        <div className="SubscriptionSelectionOption__tag">{tagLabel}</div>
        <div className="SubscriptionSelectionOption__card-content">
          <div className="SubscriptionSelectionOption__card-content-info">
            <div className="SubscriptionSelectionOption__card-content-info-description">
              {membershipTitle}
            </div>
            <div className="SubscriptionSelectionOption__card-content-info-value">
              {isOneTimePurchase ? (
                <span className="subtotal">{`$${oneTimePrice}`}</span>
              ) : (
                <span className="value">{`$${unlimitedPrice}`}</span>
              )}
              {isOneTimePurchase && (
                <span className="interval-copy">
                  {ONE_TIME_SUBTITLE}
                </span>
              )}
              {!isOneTimePurchase && (
                <span className="interval-copy">
                  {UNLIMITED_SUBTITLE}
                </span>
              )}
            </div>
            <div className="SubscriptionSelectionOption__card-content-info-divider" />
            {isOneTimePurchase && (
              <div className="SubscriptionSelectionOption__card-content-info-items">
                {oneTimeBenefits.map(benefit => (
                  <div key={benefit} className="SubscriptionSelectionOption__card-content-info-item">
                    <span className="SubscriptionSelectionOption__check-icon" aria-hidden="true">
                      <CheckSvg stroke="#FF4286" strokeWidth={1.25} />
                    </span>
                    <span className="SubscriptionSelectionOption__card-content-info-item-title">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {!isOneTimePurchase && (
              <div className="SubscriptionSelectionOption__card-content-info-items">
                {unlimitedBenefits.map(benefit => (
                  <div key={benefit} className="SubscriptionSelectionOption__card-content-info-item">
                    <span className="SubscriptionSelectionOption__check-icon" aria-hidden="true">
                      <CheckSvg stroke="#FF4286" strokeWidth={1.25} />
                    </span>
                    <span className="SubscriptionSelectionOption__card-content-info-item-title">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {!isOneTimePurchase && (
              <div className="SubscriptionSelectionOption__card-content-info-breakeven">
                {UNLIMITED_BREAKEVEN_COPY}
              </div>
            )}
            {!isOneTimePurchase && (
              <button
                type="button"
                className="SubscriptionSelectionOption__card-content-info-cta-subscription"
                onClick={handleCtaClick}
              >
                {UNLIMITED_CTA_COPY}
              </button>
            )}
            {isOneTimePurchase && (
              <>
                <div className="SubscriptionSelectionOption__card-content-info-warning">
                  {ONE_TIME_WARNING_COPY}
                </div>
                <button
                  type="button"
                  className="SubscriptionSelectionOption__card-content-info-cta"
                  onClick={handleCtaClick}
                >
                  {ONE_TIME_CTA_COPY}
                </button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionSelectionOptions;
